import { focusManager } from '@tanstack/react-query';

import { accessTokenNeedsRefresh } from '../auth/jwtSchedule';
import { scheduleAccessTokenRefresh } from '../auth/scheduleAccessTokenRefresh';
import { getAccessToken } from '../auth/tokens';
import { runRegisteredRefresh } from '../auth/useAuthBootstrap';

/**
 * Wire TanStack Query's window-focus refetch to `document.visibilityState`.
 * Call once at app startup after creating the QueryClient.
 *
 * When a tab returns after being idle with an expired access token,
 * refresh **before** focus refetches so queries do not race ahead with
 * a dead Bearer while the HttpOnly refresh cookie is still valid.
 */
export function setupWebQueryFocusManager(): void {
  focusManager.setEventListener(handleFocus => {
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      if (!visible) {
        handleFocus(false);
        return;
      }

      const token = getAccessToken();
      if (token) {
        scheduleAccessTokenRefresh(token);
      }

      if (accessTokenNeedsRefresh(token)) {
        void runRegisteredRefresh().finally(() => {
          handleFocus(true);
        });
        return;
      }

      handleFocus(true);
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  });
}
